import { Link, Outlet } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import spoonityLogo from '@/assets/spoonity-logo.svg';
import { Btn } from '@/components/Btn';
import { useI18n } from '@/i18n/I18nProvider';
import { useSession } from '@/session/SessionProvider';
import { LanguageToggle } from '@/components/LanguageToggle';
import { useState } from 'react';
import { ErrorState } from '@/components/States';
import { TestAccountBanner, isTestCustomer } from '@/components/TestAccountBanner';

const PRIVACY_URL = 'https://www.spoonity.com/privacy-policy';

/**
 * Page frame: skip link, header with brand, the always visible language toggle and sign out,
 * the main landmark, and the footer (help, privacy, who is signed in). Everything that is not
 * the page content is hidden when printing.
 */
export function Layout() {
  const { t } = useI18n();
  const session = useSession();
  const signedIn = session.status === 'authenticated';
  const [signOutError, setSignOutError] = useState<unknown>(null);
  // Every account of this sign-in is the verification test account: the banner belongs to the frame, so it is on every screen
  // (accounts list included). A mix of test and real accounts is flagged per account instead (AccountLayout).
  const allTest = signedIn && session.customers.length > 0 && session.customers.every(isTestCustomer);

  async function run(fn: () => Promise<void>) {
    setSignOutError(null);
    try {
      await fn();
    } catch (e) {
      if (!session.handleSessionError(e)) setSignOutError(e);
    }
  }

  return (
    <div className="bg-background text-typography-primary flex min-h-screen flex-col">
      <a
        href="#main"
        className="bg-surface-inverse-primary text-typography-inverse-primary sr-only z-50 rounded-button px-400 py-300 focus:not-sr-only focus:fixed focus:top-200 focus:left-200"
      >
        {t('app.skipToContent')}
      </a>
      <header className="bg-surface-primary border-border-primary border-b print:hidden">
        <div className="mx-auto flex w-full max-w-[1040px] items-center justify-between gap-200 px-400 py-200">
          <Link to="/" className="focus-ring flex min-h-[44px] min-w-0 items-center rounded-button">
            <img src={spoonityLogo} alt={t('app.title')} width={247} height={76} className="h-[36px] w-auto shrink-0" />
          </Link>
          <div className="flex shrink-0 items-center gap-100">
            <LanguageToggle />
            {signedIn && (
              <Btn type="button" variant="secondary" size="lg" className="min-h-[44px]" onClick={() => void run(session.signOut)}>
                <LogOut size={18} aria-hidden="true" />
                <span className="sr-only sm:not-sr-only">{t('nav.signOut')}</span>
              </Btn>
            )}
          </div>
        </div>
      </header>

      <main id="main" tabIndex={-1} className="mx-auto w-full max-w-[1040px] flex-1 px-400 py-600 outline-none sm:py-800">
        {allTest ? <TestAccountBanner className="mb-400" /> : null}
        {signOutError ? <ErrorState error={signOutError} className="mb-400" /> : null}
        <Outlet />
      </main>

      <footer className="border-border-primary text-footnote text-typography-secondary border-t print:hidden">
        <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-300 px-400 py-500">
          <p>{t('footer.help')}</p>
          <div className="flex flex-wrap items-center gap-x-500 gap-y-100">
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="focus-ring text-typography-primary inline-flex min-h-[44px] items-center underline">
              {t('footer.privacy')}
            </a>
            {signedIn && (
              <>
                {session.me && <span>{t('nav.signedInAs', { email: session.me.maskedEmail })}</span>}
                <button
                  type="button"
                  onClick={() => void run(session.signOutAll)}
                  className="focus-ring text-typography-primary inline-flex min-h-[44px] items-center underline"
                >
                  {t('nav.signOutAll')}
                </button>
              </>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
